import { Injectable } from '@angular/core';
import { Pattern } from '../core-data-model/Pattern.model';
import {
    PatternInsights,
    FactorInfo
} from '../core-data-model/PatternInsights.interface';
import { Person } from '../core-data-model/Person.model';
import {
    ReferencePatternType,
    ReferencePatternsInformation
} from '../core-data-model/ReferencePatternType.model';
import { Util } from '../core-utilities/Util';

// This describes the result of an API call to the server
// that should have the complete data to generate the
// report - note that optional fields are ones I know
// are not used and do not need to be generated, but are
// generated by the server
export type PortablePersonPageServerData = {
    firstName: string;
    lastName: string;
    selfAssessmentScore: {
        aPercentile?: number;
        bPercentile?: number;
        cPercentile?: number;
        dPercentile?: number;
        ePercentile?: number;
        mPercentile?: number;
        formId?: number;
        selfConceptFormId?: string;
        piSurveyId?: number;
        piPatternGraphId?: number;
        patternNumber?: number;
        piSurveyScoreId?: number;
        scoringMethodId?: number;
        assessmentId?: string;
        scoringNormId?: number;
        aSigma: number;
        bSigma: number;
        cSigma: number;
        dSigma: number;
        eSigma?: number;
        mTotal?: number;
        aSum?: number;
        bSum?: number;
        cSum?: number;
        dSum?: number;
        eSum?: number;
        blankSum?: number;
        createDate: string;
    };
    guidebook: {
        factors: FactorInfo[];
        referencePattern: {
            patternNumber: number;
            name?: string;
            aSigma?: number;
            bSigma?: number;
            cSigma?: number;
            dSigma?: number;
            singularName?: string;
            pluralName?: string;
            description?: string;
            traits?: string[];
            badgeUrl?: string;
            jobBadgeUrl?: string;
            challenges?: string[];
            moreInfoLink?: string;
        };
        factorEmphasisCombination: {
            factorCombinations: {
                typeLocalizedDescription?: string;
                traits: string[];
                type: number;
                midpointRelationship: number;
                factorDistance?: number;
                factorDifference: number;
                primaryTrait: string;
            }[];
            aSigmaZone?: number;
            bSigmaZone?: number;
            cSigmaZone?: number;
            dSigmaZone?: number;
            ASigmaZone?: number;
            BSigmaZone?: number;
            CSigmaZone?: number;
            DSigmaZone?: number;
        };
        selfConcept?: {
            dominanceTraits: string[];
            dominanceRelationshipToSelf: number;
            extraversionTraits: string[];
            extraversionRelationshipToSelf: number;
            patienceTraits: string[];
            patienceRelationshipToSelf: number;
            formalityTraits: string[];
            formalityRelationshipToSelf: number;
        };
    };
    strongestBehaviorContents: string[];
    scoreId: string;
    analystEmail: string;
};

// This describes what the image app needs
export type PortablePersonPageForImage = {
    firstName: string;
    lastName: string;
    patternInsights: PatternInsights;
    referencePattern: ReferencePatternType;
    pattern: Pattern;
    date: Date;
    scoreId: string;
    analystEmail: string;
};

@Injectable({
    providedIn: 'root'
})
export class PortablePersonPageImageTransform {
    constructor(private util: Util) {}

    transformFromServerToImageApp(
        data: PortablePersonPageServerData
    ): PortablePersonPageForImage {
        return {
            firstName: data.firstName,
            lastName: data.lastName,
            patternInsights: {
                factorInfo: data.guidebook.factors,
                factorCombinationInfo:
                    data.guidebook.factorEmphasisCombination.factorCombinations,
                strongestBehaviorContents: data.strongestBehaviorContents
            },
            referencePattern: ReferencePatternsInformation.find(
                (profile) =>
                    profile.id === data.guidebook.referencePattern.patternNumber
            ),
            pattern: new Pattern({
                A: data.selfAssessmentScore.aSigma,
                B: data.selfAssessmentScore.bSigma,
                C: data.selfAssessmentScore.cSigma,
                D: data.selfAssessmentScore.dSigma
            }),
            date: this.util.newDate(data.selfAssessmentScore.createDate),
            scoreId: data.scoreId,
            analystEmail: data.analystEmail
        };
    }

    transformFromSpaToServer(
        personDetails: Person,
        patternInsights: PatternInsights,
        analystEmail: string
    ): PortablePersonPageServerData {
        return {
            firstName: personDetails.firstName,
            lastName: personDetails.lastName,
            selfAssessmentScore: {
                aSigma: personDetails.originalAssessment.pattern.self.A,
                bSigma: personDetails.originalAssessment.pattern.self.B,
                cSigma: personDetails.originalAssessment.pattern.self.C,
                dSigma: personDetails.originalAssessment.pattern.self.D,
                createDate:
                    personDetails.originalAssessment.completedDate.toString()
            },
            guidebook: {
                factors: patternInsights.factorInfo,
                referencePattern: {
                    patternNumber: personDetails.referencePattern.id
                },
                factorEmphasisCombination: {
                    factorCombinations: patternInsights.factorCombinationInfo
                }
            },
            strongestBehaviorContents:
                patternInsights.strongestBehaviorContents,
            scoreId: personDetails.originalAssessment.pattern.scoreId,
            analystEmail: analystEmail
        };
    }
}
